import * as React from "react";
import Select, { MultiValue, StylesConfig, components, DropdownIndicatorProps, OptionProps, MultiValueProps } from "react-select";
import { cn } from "@/lib/utils";
import makeAnimated from 'react-select/animated';
import { ChevronsUpDown } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";

export interface Option {
    label: string;
    value: string;
    avatarUrl?: string;
    email?: string;
}

const DropdownIndicator = (props: DropdownIndicatorProps<Option, true>) => {
    return (
        <components.DropdownIndicator {...props}>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </components.DropdownIndicator>
    );
};

const CustomOption = (props: OptionProps<Option, true>) => {
    const { data } = props;
    return (
        <components.Option {...props}>
            <div className="flex items-center gap-2.5 w-full py-0.5">
                <UserAvatar 
                    src={data.avatarUrl} 
                    name={data.label} 
                    className="h-7 w-7 shrink-0"
                    fallbackClassName="text-[10px]"
                />
                <span className="truncate flex-1">{data.label}</span>
            </div>
        </components.Option>
    );
};

const CustomMultiValue = (props: MultiValueProps<Option, true>) => {
    const { data } = props;
    return (
        <components.MultiValue {...props}>
            <div className="flex items-center gap-1.5 pl-1">
                <UserAvatar 
                    src={data.avatarUrl} 
                    name={data.label} 
                    className="h-5 w-5 shrink-0"
                    fallbackClassName="text-[8px]"
                />
                <span className="text-xs pr-1">{data.label}</span>
            </div>
        </components.MultiValue>
    );
};

interface MultiSelectProps {
    options: Option[];
    selected: string[];
    onChange: (selected: string[]) => void;
    onSearchChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    loading?: boolean;
    initialOptions?: Option[];
}

export function MultiSelect({
    options,
    selected,
    onChange,
    onSearchChange,
    placeholder = "Select options...",
    className,
    loading,
    initialOptions = [],
}: MultiSelectProps) {
    // Keep a local cache of options to ensure labels persist even when they are filtered out
    const [allOptions, setAllOptions] = React.useState<Option[]>(() => {
        const unique = new Map<string, Option>();
        [...initialOptions, ...options].forEach(opt => unique.set(opt.value, opt));
        return Array.from(unique.values());
    });

    React.useEffect(() => {
        if (options.length > 0) {
            setAllOptions(prev => {
                const unique = new Map<string, Option>();
                prev.forEach(opt => unique.set(opt.value, opt));
                options.forEach(opt => unique.set(opt.value, opt));
                return Array.from(unique.values());
            });
        }
    }, [options]);

    const value = React.useMemo(() => 
        selected.map(val =>
            allOptions.find(opt => opt.value === val) || { label: val, value: val }
        ),
        [selected, allOptions]
    );

    const handleChange = (newValue: MultiValue<Option>) => {
        onChange(newValue.map(v => v.value));
    };

    // React-select custom styles to match Shadcn/Tailwind design
    const customStyles: StylesConfig<Option, true> = {
        control: (base, state) => ({
            ...base,
            backgroundColor: "transparent",
            fontFamily: "inherit",
            borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--input))",
            boxShadow: state.isFocused ? "0 0 0 1px hsl(var(--primary))" : "none",
            "&:hover": {
                borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--input))",
            },
            borderRadius: "var(--radius)",
            minHeight: "40px",
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "var(--radius)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 1,
            fontFamily: "inherit",
        }),
        option: (base, state) => ({
            ...base,
            fontFamily: "inherit",
            backgroundColor: state.isFocused ? "hsl(var(--accent))" : "transparent",
            color: state.isFocused ? "hsl(var(--accent-foreground))" : "inherit",
            cursor: "pointer",
            "&:active": {
                backgroundColor: "hsl(var(--accent))",
            },
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "hsl(var(--secondary))",
            borderRadius: "4px",
            color: "hsl(var(--secondary-foreground))",
            fontFamily: "inherit",
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "inherit",
            padding: "4px 8px",
            fontFamily: "inherit",
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: "hsl(var(--muted-foreground))",
            fontFamily: "inherit",
            "&:hover": {
                backgroundColor: "transparent",
                color: "hsl(var(--foreground))",
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: "hsl(var(--muted-foreground))",
            fontFamily: "inherit",
        }),
        input: (base) => ({
            ...base,
            color: "hsl(var(--foreground))",
            fontFamily: "inherit",
        }),
        singleValue: (base) => ({
            ...base,
            color: "hsl(var(--foreground))",
            fontFamily: "inherit",
        }),
    };
    const animatedComponents = makeAnimated();
    const allComponents = {
        ...animatedComponents,
        DropdownIndicator,
        ClearIndicator: () => null, // Disabled: no clear all button
        Option: CustomOption,
        MultiValue: CustomMultiValue,
        IndicatorSeparator: () => null, // Cleaner look without the vertical line
        // Disable MultiValueRemove animation to ensure immediate visual feedback
        MultiValueRemove: components.MultiValueRemove,
    };

    return (
        <div className={cn("w-full font-sans", className)}>
            <Select
                key={selected.join(',')} // Force re-render when selection changes
                isMulti
                options={options}
                value={value}
                onChange={handleChange}
                onInputChange={(val) => onSearchChange?.(val)}
                placeholder={placeholder}
                isLoading={loading}
                styles={customStyles}
                classNamePrefix="react-select"
                unstyled // We'll use the styles object to reconstruct the look but this keeps it clean
                components={allComponents}
                menuPosition="absolute"
                classNames={{
                    control: ({ isFocused }) => cn(
                        "flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                        isFocused && "ring-2 ring-ring ring-offset-2 border-primary"
                    ),
                    menu: () => "mt-2 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95 z-[1]",
                    menuList: () => "p-1",
                    option: ({ isFocused, isSelected }) => cn(
                        "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1 text-sm outline-none transition-colors",
                        isFocused && "bg-accent text-accent-foreground",
                        isSelected && "bg-accent/50"
                    ),
                    valueContainer: () => "flex flex-wrap gap-1 p-0.5",
                    multiValue: () => "flex items-center rounded-md bg-secondary px-0.5 py-0.5 text-xs font-medium text-secondary-foreground",
                    multiValueLabel: () => "",
                    multiValueRemove: () => "rounded-sm hover:bg-destructive hover:text-destructive-foreground transition-colors",
                    noOptionsMessage: () => "py-6 text-center text-xs text-muted-foreground",
                    loadingMessage: () => "py-6 text-center text-xs text-muted-foreground",
                }}
            />
        </div>
    );
}
